// Legacy → canonical recognition registry (TSL legacy-compatibility migration).
//
// Recognizes a quarantined legacy `data.code` custom block as an EXACT match for a known canonical
// trusted-TS implementation, using AUTHORITATIVE signals only — never the display name:
//   • block type / category,
//   • declared entry-point function(s),
//   • required parameter names (the canonical reads exactly these),
//   • characteristic engine-context fields the body reads,
//   • a normalized-source hash of known-good legacy bodies (exact fast-path).
// A block auto-migrates ONLY on an EXACT match (hash, or full structural signature: type + all entry
// points + exact param set + all ctx fields). Anything partial/unknown stays quarantined — never guess.
//
// The canonical IDs are the EXACT markers already dispatched by the trusted-TS registries
// (src/engine/riskRules.ts CANONICAL_CHECKBAR_FNS + src/engine/qtyCalculator.ts CANONICAL_QTY). The
// canonical implementations read the SAME parameter names the legacy bodies use (verified), so migration
// is: stamp `parameters.__canonical = <id>`, keep params + connections, drop `data.code`.

export interface CanonicalDescriptor {
  canonicalId: string;          // exact trusted-TS marker, e.g. 'max_daily_loss_pct_v1'
  canonicalVersion: number;
  blockType: string;            // 'qty_calculator' | 'risk_rule'
  entryFns: string[];           // ALL must be declared in the body
  requiredParams: string[];     // the body's params.X set must equal this exactly
  ctxFields: string[];          // characteristic ctx.X reads — all must be present
  bodyHashes: string[];         // normalized-source sha256 hex of known-good legacy bodies (exact fast-path)
  label: string;                // human label for the migration report
}

/** Normalize a legacy body for hashing/structure: drop comments, collapse whitespace. Stable + canonical. */
export function normalizeBody(code: string): string {
  return String(code)
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\/\/[^\n]*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** SHA-256 (hex) of the normalized body. Uses Web Crypto (browser + Deno + Node ≥ 20). */
export async function hashBody(code: string): Promise<string> {
  const data = new TextEncoder().encode(normalizeBody(code));
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const fnSet = (code: string) => new Set([...code.matchAll(/function\s+([A-Za-z_]\w*)\s*\(/g)].map((m) => m[1]));
const paramSet = (code: string) => new Set([...code.matchAll(/params\.([A-Za-z_]\w*)/g)].map((m) => m[1]));
const ctxSet = (code: string) => new Set([...code.matchAll(/ctx\.([A-Za-z_]\w*)/g)].map((m) => m[1]));
const subset = (need: Iterable<string>, have: Set<string>) => [...need].every((x) => have.has(x));
const sameSet = (need: string[], have: Set<string>) => have.size === need.length && need.every((x) => have.has(x));

// The 7 supported canonical mappings (FTMO prop-firm family). bodyHashes seed the exact fast-path with the
// known-good legacy bodies; the structural signature recognizes formatting variants of the same logic.
export const CANONICAL_REGISTRY: CanonicalDescriptor[] = [
  {
    canonicalId: 'ftmo_calcqty_v1', canonicalVersion: 1, blockType: 'qty_calculator', label: 'calcQty (risk %, integer contracts)',
    entryFns: ['computeQty'], ctxFields: ['dayStartEquity', 'equity', 'stopDistance'],
    requiredParams: ['maxContracts', 'maxDailyLossPct', 'mdlBufferPct', 'minContracts', 'minStopPoints', 'pointValue', 'riskPct', 'rtCommission'],
    bodyHashes: ['a8fa1cb84229ba6b'],
  },
  {
    canonicalId: 'max_trades_per_day_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'maxTradesPerDay (1/day cap)',
    entryFns: ['checkEntry', 'onTradeClosed', 'onDayRollover'], ctxFields: ['barLocalDate'],
    requiredParams: ['enabled', 'maxTradesPerDay'], bodyHashes: ['628b22fe727873bc'],
  },
  {
    canonicalId: 'max_daily_loss_pct_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'maxDailyLossPct',
    entryFns: ['checkEntry', 'onTradeClosed', 'onDayRollover'], ctxFields: ['accountSize', 'barUtcDate', 'enforcementMode'],
    requiredParams: ['accountSize', 'enabled', 'maxDailyLossPct'], bodyHashes: ['83bbab669bd22ae0'],
  },
  {
    canonicalId: 'max_total_loss_pct_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'maxTotalLossPct',
    entryFns: ['checkEntry', 'onTradeClosed'], ctxFields: ['accountSize', 'enforcementMode'],
    requiredParams: ['accountSize', 'enabled', 'maxTotalLossPct'], bodyHashes: ['f354675b34f16e63'],
  },
  {
    canonicalId: 'trailing_drawdown_pct_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'trailingDrawdownPct',
    entryFns: ['checkEntry', 'onTradeClosed'], ctxFields: ['accountSize', 'enforcementMode', 'equity', 'peakEquity'],
    requiredParams: ['accountSize', 'enabled', 'trailingDrawdownPct'], bodyHashes: ['5dd15d8875e06b11'],
  },
  {
    canonicalId: 'daily_profit_lock_pct_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'dailyProfitLockPct',
    entryFns: ['checkEntry', 'onTradeClosed', 'onDayRollover'], ctxFields: ['barUtcDate', 'dayStartEquity', 'enforcementMode', 'equity'],
    requiredParams: ['dailyProfitLockPct', 'enabled'], bodyHashes: ['3b7e33dfb98b784e'],
  },
  {
    canonicalId: 'flatten_at_session_close_v1', canonicalVersion: 1, blockType: 'risk_rule', label: 'flattenAtSessionClose',
    entryFns: ['parseHHMM', 'checkBar'], ctxFields: ['barLocalMinutes', 'isLastDataBar', 'nextBarLocalMinutes'],
    requiredParams: ['enabled', 'sessionEnd'], bodyHashes: ['543c68c84e766f00'],
  },
];

export interface MatchResult { descriptor: CanonicalDescriptor; confidence: 'exact-hash' | 'exact-structural'; reason: string; }

/**
 * Match a legacy block against the registry. Returns the canonical descriptor + confidence on an EXACT
 * match (hash OR full structural signature), or null. Never returns a partial/guessed match.
 *  - `normalizedHashPrefix`: pass a precomputed 16-hex-char prefix of hashBody() for the exact fast-path
 *    (the UI computes it async). If omitted, only structural matching is attempted.
 */
export function matchLegacyBlock(
  block: { type?: string; data?: { blockType?: string; code?: string; parameters?: Record<string, unknown> } },
  normalizedHashPrefix?: string,
): MatchResult | null {
  if (block?.type !== 'custom') return null;
  const code = typeof block.data?.code === 'string' ? block.data.code : '';
  if (!code.trim()) return null;
  const bt = block.data?.blockType;
  const fns = fnSet(code), prms = paramSet(code), ctxs = ctxSet(code);

  for (const d of CANONICAL_REGISTRY) {
    if (bt !== d.blockType) continue;
    // exact fast-path: normalized body hash (16-hex prefix) is a known-good canonical body.
    if (normalizedHashPrefix && d.bodyHashes.includes(normalizedHashPrefix)) {
      return { descriptor: d, confidence: 'exact-hash', reason: `normalized body hash matches a known ${d.canonicalId} body` };
    }
    // exact structural signature: type + ALL entry fns + EXACT param set + ALL ctx fields.
    if (subset(d.entryFns, fns) && sameSet(d.requiredParams, prms) && subset(d.ctxFields, ctxs)) {
      return { descriptor: d, confidence: 'exact-structural', reason: `${d.blockType} with entry ${d.entryFns.join('/')}, exact param set, and ${d.ctxFields.length} canonical ctx fields` };
    }
  }
  return null;
}
